import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useTellescopeInfiniteQuery } from "@tellescope/tanstack-query";
import type { ConversationPreview } from "../types";
import { ROOT_CONVERSATION_TYPES } from "../types";

interface ConversationMessage {
  role: string;
  text: string;
}

interface ConversationRecord {
  id: string;
  messages?: ConversationMessage[];
  updatedAt: Date;
}

export const useConversationList = () => {
  const { t } = useTranslation("ai");
  const query = useTellescopeInfiniteQuery({
    model: "ai_conversations",
    method: "getSome",
    request: {
      mdbFilter: { type: { $in: [...ROOT_CONVERSATION_TYPES] } },
      limit: 20,
    },
  });

  const conversations = useMemo<ConversationPreview[]>(() => {
    if (!query.data?.pages) return [];

    return query.data.pages
      .flat()
      .map((conv: ConversationRecord) => {
        const firstUserMsg = conv.messages?.find((m) => m.role === "user");
        let preview = t("No preview");
        if (firstUserMsg) {
          try {
            const payload = JSON.parse(firstUserMsg.text);
            preview = payload.content ?? firstUserMsg.text;
          } catch {
            preview = firstUserMsg.text;
          }
        }
        return {
          id: conv.id,
          preview: preview.slice(0, 100),
          updatedAt: conv.updatedAt,
        };
      })
      .sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());
  }, [query.data?.pages, t]);

  return {
    conversations,
    isLoading: query.isLoading,
    error: query.error,
    loadMore: query.fetchNextPage,
    hasMore: query.hasNextPage ?? false,
  };
};
